import { LangCode } from "@/lib/gql/__generated__/graphql";

import { useEditedQuestionIndicator, useErrorQuestionIndicator } from "../../../../_shared/hooks";
import { useSendCustomMailSettingForReservationCancel } from "../../../api/send-custom-mail-setting/reservation-cancel";
import { useSendCustomMailSettingForReservationCreate } from "../../../api/send-custom-mail-setting/reservation-create";
import { useSendCustomMailSettingForReservationUpdate } from "../../../api/send-custom-mail-setting/reservation-update";
import { MailTemplateItem } from "../_shared/mail-template-item";

import type { BasicCustomMailSettingFormData } from "../../../schema";
import type { UseFormReturn } from "react-hook-form";

type Props = {
	form: UseFormReturn<BasicCustomMailSettingFormData>;
	hotelID: string;
	hotelEmailAddress: string;
};

export const ForTriggeredByReserve = ({ form, hotelID, hotelEmailAddress }: Props) => {
	const {
		watch,
		setValue,
		formState: { errors },
	} = form;

	const createdValue = watch("forReservation.atCreated.text");
	const updatedValue = watch("forReservation.atUpdated.text");
	const canceledValue = watch("forReservation.atCanceled.text");

	// 値が入っているかどうかを監視（設定した項目がある場合に青丸を表示）
	useEditedQuestionIndicator("予約成立時", Boolean(createdValue));
	useEditedQuestionIndicator("予約が変更された時", Boolean(updatedValue));
	useEditedQuestionIndicator("予約がキャンセルされた時", Boolean(canceledValue));

	// バリデーションエラーがある場合に赤丸を表示
	useErrorQuestionIndicator("予約成立時", Boolean(errors.forReservation?.atCreated?.text));
	useErrorQuestionIndicator("予約が変更された時", Boolean(errors.forReservation?.atUpdated?.text));
	useErrorQuestionIndicator("予約がキャンセルされた時", Boolean(errors.forReservation?.atCanceled?.text));

	const { sendTestMailAtReservationCreate } = useSendCustomMailSettingForReservationCreate();
	const { sendTestMailAtReservationUpdate } = useSendCustomMailSettingForReservationUpdate();
	const { sendTestMailAtReservationCancel } = useSendCustomMailSettingForReservationCancel();

	const handleSendTestEmailForCreate = async ({ email }: { email: string }) => {
		await sendTestMailAtReservationCreate({
			mailAddress: email,
			customMessage: createdValue || "",
			id: hotelID,
			langCode: LangCode.Default,
		});
	};

	const handleSendTestEmailForUpdate = async ({ email }: { email: string }) => {
		await sendTestMailAtReservationUpdate({
			mailAddress: email,
			customMessage: updatedValue || "",
			id: hotelID,
			langCode: LangCode.Default,
		});
	};

	const handleSendTestEmailForCancel = async ({ email }: { email: string }) => {
		await sendTestMailAtReservationCancel({
			mailAddress: email,
			customMessage: canceledValue || "",
			id: hotelID,
			langCode: LangCode.Default,
		});
	};

	return (
		<div className="space-y-6">
			{/* 予約成立時 */}
			<div className="ml-2">
				<MailTemplateItem
					hidePreviewButton={false}
					label={"予約成立時"}
					dialogTitle={"予約成立時"}
					value={createdValue || ""}
					errorMessage={errors.forReservation?.atCreated?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atCreated.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForCreate}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
				/>
				{/* 予約が変更された時 */}
				<MailTemplateItem
					hidePreviewButton={false}
					label={"予約が変更された時"}
					dialogTitle={"予約が変更された時"}
					value={updatedValue || ""}
					errorMessage={errors.forReservation?.atUpdated?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atUpdated.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForUpdate}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
				/>
				{/* 予約がキャンセルされた時 */}
				<MailTemplateItem
					hidePreviewButton={false}
					label={"予約がキャンセルされた時"}
					dialogTitle={"予約がキャンセルされた時"}
					value={canceledValue || ""}
					errorMessage={errors.forReservation?.atCanceled?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atCanceled.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForCancel}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
				/>
			</div>
		</div>
	);
};
